import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, AlertTriangle } from 'lucide-react';
import DashboardPage from '../../components/DashboardPage';

// Mock data - In a real app, this would come from your API
const mockData = {
  monthlySpending: [
    { category: 'Housing', amount: 2000 },
    { category: 'Food', amount: 800 },
    { category: 'Transport', amount: 400 },
    { category: 'Entertainment', amount: 300 },
    { category: 'Utilities', amount: 250 },
    { category: 'Shopping', amount: 400 },
  ],
  trends: [
    { month: 'Jan', amount: 3950 },
    { month: 'Feb', amount: 4100 },
    { month: 'Mar', amount: 3800 },
    { month: 'Apr', amount: 4150 },
    { month: 'May', amount: 4300 },
  ],
  insights: [
    {
      type: 'warning',
      message: 'Entertainment spending increased by 15% this month'
    },
    {
      type: 'success',
      message: 'You saved $200 on utilities compared to last month'
    },
    {
      type: 'info',
      message: 'Your biggest expense category is Housing at 45% of total spending'
    }
  ]
};

const COLORS = ['#68A511', '#059669', '#0284c7', '#7c3aed', '#db2777', '#dc2626'];

const SpendingAnalytics = () => {
  const [data, setData] = useState(mockData);
  const [selectedTimeframe, setSelectedTimeframe] = useState('month');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate API call
    const fetchData = async () => {
      setIsLoading(true);
      // In a real app, this would be an API call
      setTimeout(() => {
        setData(mockData);
        setIsLoading(false);
      }, 1000);
    };

    fetchData();
  }, [selectedTimeframe]);

  const totalSpending = data.monthlySpending.reduce((sum, item) => sum + item.amount, 0);
  const monthlyChange = ((data.trends[4].amount - data.trends[3].amount) / data.trends[3].amount) * 100;

  return (
    <DashboardPage
      title="Spending Analytics"
      subtitle="Analyze and track your spending patterns"
    >
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-pulse">
          <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
          <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-[#68A511]/10 rounded-lg">
                  <DollarSign className="w-6 h-6 text-[#68A511]" />
                </div>
                <span className={`flex items-center ${
                  monthlyChange >= 0 ? 'text-green-500' : 'text-red-500'
                }`}>
                  {monthlyChange >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  <span className="ml-1">{Math.abs(monthlyChange).toFixed(1)}%</span>
                </span>
              </div>
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Spending</h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                ${totalSpending.toLocaleString()}
              </p>
            </motion.div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Category Distribution */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Spending by Category
              </h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.monthlySpending}
                      dataKey="amount"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label
                    >
                      {data.monthlySpending.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            {/* Monthly Trend */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Spending Trend
              </h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="amount" fill="#68A511" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          </div>

          {/* Insights */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Spending Insights
            </h3>
            {data.insights.map((insight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`p-4 rounded-lg border ${
                  insight.type === 'warning'
                    ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20'
                    : insight.type === 'success'
                    ? 'bg-green-50 border-green-200 dark:bg-green-900/20'
                    : 'bg-blue-50 border-blue-200 dark:bg-blue-900/20'
                }`}
              >
                <div className="flex items-center">
                  <AlertTriangle className={`w-5 h-5 ${
                    insight.type === 'warning'
                      ? 'text-yellow-600'
                      : insight.type === 'success'
                      ? 'text-green-600'
                      : 'text-blue-600'
                  }`} />
                  <p className="ml-3 text-sm text-gray-600 dark:text-gray-300">
                    {insight.message}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </>
      )}
    </DashboardPage>
  );
};

export default SpendingAnalytics; 